// src/components/FormSelectionView.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Spin,
  Alert,
  Select,
  Button,
  Space,
  Typography,
  Tag,
  Empty,
  notification,
  Table,
  Tooltip,
  Badge,
  Modal,
  Input,
  Pagination,
  message,
  Divider,
  Switch,
} from "antd";
import {
  DatabaseOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  ApartmentOutlined,
  LeftOutlined,
  StarOutlined,
  SearchOutlined,
  CalculatorOutlined,
  PlusOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  TableOutlined,
  BarChartOutlined,
  PieChartOutlined,
  FullscreenOutlined,
  NumberOutlined,
  PercentageOutlined,
  FieldNumberOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

// ==================== API CONFIGURATION ====================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:5000";

// ==================== COLORS ====================
const PIE_COLORS = [
  "#1890ff", "#52c41a", "#fa8c16", "#f5222d", "#722ed1",
  "#13c2c2", "#eb2f96", "#faad14", "#a0d911", "#2f54eb",
];

const CARD_START_COLORS = [
  '#1a6b8a', '#c0392b', '#4c1d95', '#14532d', '#9d174d', '#1e3a5f',
  '#92400e', '#1e293b', '#be185d', '#0e7490', '#3b0764', '#365314',
];

const CARD_END_COLORS = [
  '#0d9488', '#e67e22', '#7c3aed', '#16a34a', '#db2777', '#2563eb',
  '#d97706', '#475569', '#f97316', '#38bdf8', '#a21caf', '#65a30d',
];

const DATAPOINT_GRADIENT_SHADES = [
  { start: '#008ae0', end: '#a0d0e2' },
  { start: '#e000e4', end: '#d98eda' },
  { start: '#b90000', end: '#f0c8c8' },
  { start: '#02cb4f', end: '#afe1c3' },
  { start: '#c25405', end: '#dbb8a2' },
  { start: '#ce0059', end: '#dbb1c1' },
  { start: '#29811d', end: '#8af586' },
];

// ==================== HELPER FUNCTIONS ====================

const formatFieldName = (fieldName) => {
  if (!fieldName) return "Unknown Field";
  if (typeof fieldName !== 'string') return String(fieldName);
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getApprovedSubmissionsOnly = (formId, data) => {
  try {
    const hasWorkflowColumn = data.length > 0 && 'workflow_action' in data[0];
    if (!hasWorkflowColumn) {
      console.log(`📊 Form ${formId}: No workflow_action column found`);
      return [];
    }
    const approvedData = data.filter(row => {
      const action = row.workflow_action;
      return action === 'approve' || action === 'approved' ||
        action === 'APPROVED' || action === 'Approve' || action === 'APPROVE';
    });
    return approvedData;
  } catch (error) {
    console.error(`Error filtering approved submissions:`, error);
    return [];
  }
};

const resolveIdsInData = (data, referenceCache = {}) => {
  if (!data || !Array.isArray(data)) return data;
  return data.map(row => {
    const resolved = { ...row };
    if (referenceCache.districts && row.district_id) {
      resolved.district_name = referenceCache.districts[row.district_id]?.name || row.district_id;
    }
    if (referenceCache.divisions && row.division_id) {
      resolved.division_name = referenceCache.divisions[row.division_id]?.name || row.division_id;
    }
    if (referenceCache.ulbs && row.ulb_id) {
      resolved.ulb_name = referenceCache.ulbs[row.ulb_id]?.ulb_name || row.ulb_id;
    }
    return resolved;
  });
};

// ==================== FORM FIELD FILTER COMPONENT (WITH CORRECTED VIEW ALL) ====================
const FormFieldFilter = ({ selectedForm, formData, formFields, fieldLabels, referenceCache, onFilterChange }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [uniqueValues, setUniqueValues] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [fieldHierarchy, setFieldHierarchy] = useState({});
  const [fieldParentMap, setFieldParentMap] = useState({});
  const [formFieldsList, setFormFieldsList] = useState([]);

  const isProcessingRef = useRef(false);
  const lastFieldRef = useRef(null);

  // Helper functions for resolving IDs
  const resolveDistrictName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.districts && referenceCache.districts[id]) {
      return referenceCache.districts[id].name;
    }
    return id;
  };

  const resolveDivisionName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.divisions && referenceCache.divisions[id]) {
      return referenceCache.divisions[id].name;
    }
    return id;
  };

  const resolveUlbName = (id) => {
    if (!id || id === null || id === "") return id;
    if (referenceCache && referenceCache.ulbs && referenceCache.ulbs[id]) {
      return referenceCache.ulbs[id].ulb_name || referenceCache.ulbs[id].name;
    }
    return id;
  };

  // Clean up row - remove duplicates and ID fields
  const cleanRow = (row) => {
    if (!row) return row;
    const cleaned = { ...row };

    // Remove ID fields
    delete cleaned.district_id;
    delete cleaned.division_id;
    delete cleaned.ulb_id;
    delete cleaned.user_id;
    delete cleaned.role_id;
    delete cleaned.population_id;

    // Remove workflow fields
    delete cleaned.workflow_action;
    delete cleaned.workflow_action_at;
    delete cleaned.workflow_action_by;
    delete cleaned.workflow_action_by_name;
    delete cleaned.workflow_action_by_role;
    delete cleaned.workflow_action_comments;
    delete cleaned.workflow_sequence;
    delete cleaned._workflow_status;
    delete cleaned.submission_workflow_id;
    delete cleaned.workflow_instance_id;
    delete cleaned.workflow_status;
    delete cleaned.workflow_stage;
    delete cleaned.workflow_updated_at;
    delete cleaned.status;
    delete cleaned.progress;

    // Remove _formatted and _numeric duplicates
    Object.keys(cleaned).forEach(key => {
      if (key.endsWith('_formatted') || key.endsWith('_numeric')) {
        const baseKey = key.replace(/_formatted$|_numeric$/, '');
        if (cleaned[baseKey] !== undefined) {
          delete cleaned[key];
        }
      }
    });

    // Remove duplicate location fields (keep only name versions)
    if (cleaned.division_name && cleaned.division) delete cleaned.division;
    if (cleaned.district_name && cleaned.district) delete cleaned.district;
    if (cleaned.ulb_name && cleaned.ulb) delete cleaned.ulb;

    return cleaned;
  };

  // Normalize value for comparison
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') return value.trim();
    return String(value);
  };

  // Get unique values with proper numeric handling
  const getUniqueValues = (fieldName) => {
    if (!selectedForm || !formData || formData.length === 0) return [];
    if (isProcessingRef.current) return [];

    isProcessingRef.current = true;

    try {
      const valuesMap = new Map();

      formData.forEach(row => {
        let value = row[fieldName];

        // Resolve ID fields to names
        if (fieldName === 'district_id' || fieldName === 'district') {
          value = resolveDistrictName(value);
        } else if (fieldName === 'division_id' || fieldName === 'division') {
          value = resolveDivisionName(value);
        } else if (fieldName === 'ulb_id' || fieldName === 'ulb' || fieldName === 'ulb_name') {
          value = resolveUlbName(value);
        }

        if (value !== null && value !== undefined && value !== '') {
          const stringKey = String(value);
          if (!valuesMap.has(stringKey)) {
            valuesMap.set(stringKey, String(value));
          }
        }
      });

      const uniqueVals = Array.from(valuesMap.values());
      return uniqueVals.sort((a, b) => {
        const numA = parseFloat(a);
        const numB = parseFloat(b);
        if (!isNaN(numA) && !isNaN(numB) && String(numA) === a && String(numB) === b) {
          return numA - numB;
        }
        return a.localeCompare(b);
      });
    } catch (error) {
      console.error('Error getting unique values:', error);
      return [];
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleFieldSelect = (value) => {
    if (lastFieldRef.current === value) return;
    lastFieldRef.current = value;

    setSelectedField(value);
    setSelectedValue(null);
    setShowResults(false);
    setFilteredData([]);

    // Notify parent that filter is cleared when field changes
    if (onFilterChange) {
      onFilterChange(null, null, []);
    }

    if (value) {
      setLoadingValues(true);
      setTimeout(() => {
        const uniqueVals = getUniqueValues(value);
        setUniqueValues(uniqueVals);
        setLoadingValues(false);
      }, 10);
    } else {
      setUniqueValues([]);
      setLoadingValues(false);
    }
  };

  const handleValueSelect = (value) => {
    setSelectedValue(value);

    if (!value || !formData || !selectedField) {
      setFilteredData([]);
      setShowResults(false);
      if (onFilterChange) {
        onFilterChange(null, null, []);
      }
      return;
    }

    let numericSelectedValue = null;
    const parsedNum = parseFloat(value);
    if (!isNaN(parsedNum) && String(parsedNum) === String(value)) {
      numericSelectedValue = parsedNum;
    }

    const filtered = formData.filter(row => {
      let rowValue = row[selectedField];

      if (selectedField === 'district_id' || selectedField === 'district') {
        rowValue = resolveDistrictName(rowValue);
      } else if (selectedField === 'division_id' || selectedField === 'division') {
        rowValue = resolveDivisionName(rowValue);
      } else if (selectedField === 'ulb_id' || selectedField === 'ulb' || selectedField === 'ulb_name') {
        rowValue = resolveUlbName(rowValue);
      }

      if (rowValue === null || rowValue === undefined) return false;

      const rowValueStr = String(rowValue);
      const selectedValueStr = String(value);

      const rowNum = parseFloat(rowValueStr);
      if (!isNaN(rowNum) && numericSelectedValue !== null && rowNum === numericSelectedValue) {
        return true;
      }

      return rowValueStr.toLowerCase() === selectedValueStr.toLowerCase();
    });

    const cleanedData = filtered.map(row => cleanRow(row));
    setFilteredData(cleanedData);
    setShowResults(true);

    if (onFilterChange) {
      const fieldDisplayName = getFieldDisplayName(selectedField);
      console.log("📢 Calling onFilterChange with:", {
        field: selectedField,
        value: value,
        recordCount: cleanedData.length,
        fieldDisplayName: fieldDisplayName
      });
      onFilterChange(selectedField, value, cleanedData, fieldDisplayName);
    }

    if (cleanedData.length === 0) {
      message.warning(`No records found matching "${value}"`);
    } else {
      message.success(`Found ${cleanedData.length} records matching "${value}"`);
    }
  };

  const handleClear = () => {
    lastFieldRef.current = null;
    setSelectedField(null);
    setUniqueValues([]);
    setSelectedValue(null);
    setFilteredData([]);
    setShowResults(false);

    if (onFilterChange) {
      onFilterChange(null, null, []);
    }

    message.info('Quick filter cleared');
  };

  // Build field hierarchy - matches the main component's renderHierarchicalColumns
  const buildFieldHierarchy = async (formId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema`,
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
        },
      );

      if (response.data.success && response.data.fields) {
        const fields = response.data.fields;
        const parentMap = {};
        const hierarchy = {};

        fields.forEach((field) => {
          const parentId = field.parent_id || field.parentId || field.parent || null;
          const fieldInfo = {
            fieldId: field.field_id,
            columnName: field.column_name || field.field_id,
            type: field.field_type,
            enLabel: field.en_label,
            mrLabel: field.mr_label,
            parentId: parentId,
            headingLevel: field.heading_level || null,
          };
          hierarchy[field.field_id] = fieldInfo;
          if (parentId) {
            if (!parentMap[parentId]) parentMap[parentId] = [];
            parentMap[parentId].push(fieldInfo);
          }
        });

        setFieldParentMap(parentMap);
        setFieldHierarchy(hierarchy);
      }
    } catch (error) {
      console.error("Error building field hierarchy:", error);
    }
  };

  // Get field display name
  const getFieldDisplayName = (field) => {
    if (fieldLabels && fieldLabels[field]) {
      return fieldLabels[field].en || field;
    }
    return formatFieldName(field);
  };

  // Format field name
  const formatFieldName = (fieldName) => {
    if (!fieldName) return "Unknown Field";
    if (typeof fieldName === "object") {
      fieldName = fieldName.id || fieldName.label || fieldName.value || String(fieldName);
    }
    let name = String(fieldName);
    name = name.replace(/node_\d+_/g, "").replace(/_\d+_/g, "_").replace(/\./g, " - ").replace(/:/g, " - ").replace(/\s+/g, " ").trim();
    name = name.replace(/[_-]/g, " ");
    
    const fieldMappings = {
      division_id: "Division",
      division_name: "Division Name",
      district_id: "District",
      district_name: "District Name",
      ulb_id: "ULB",
      ulb_name: "ULB Name",
    };
    
    if (fieldMappings[name]) return fieldMappings[name];
    return name.replace(/\b\w/g, (char) => char.toUpperCase()).trim() || String(fieldName);
  };

  // ========== GET ALL COLUMNS - MATCHING MAIN COMPONENT'S LOGIC ==========
  const getAllColumns = () => {
    console.log("🔍 [DEBUG] getAllColumns - Starting column extraction");

    if (!filteredData.length) {
      console.log("🔍 [DEBUG] getAllColumns - No filtered data, returning empty array");
      return [];
    }

    const sampleRow = filteredData[0];
    console.log("🔍 [DEBUG] getAllColumns - Sample row keys:", Object.keys(sampleRow));

    // EXCLUDE COLUMNS - matches main component
    const EXCLUDE_COLUMNS = [
      'workflow_action', 'workflow_action_at', 'workflow_action_by',
      'workflow_action_by_name', 'workflow_action_by_role', 'workflow_action_comments',
      'workflow_sequence', '_workflow_status', 'submission_workflow_id',
      'workflow_instance_id', 'workflow_status', 'workflow_stage', 'workflow_updated_at',
      'progress', 'status'
    ];

    // Get all keys from the cleaned data
    let allKeys = Object.keys(sampleRow);
    console.log("🔍 [DEBUG] getAllColumns - Initial keys count:", allKeys.length);

    // Remove any remaining _formatted or _numeric fields
    allKeys = allKeys.filter(key => !key.endsWith('_formatted') && !key.endsWith('_numeric'));
    
    // Remove ID fields
    allKeys = allKeys.filter(key => !key.endsWith('_id') && key !== 'id');
    
    // Remove workflow/internal fields
    allKeys = allKeys.filter(key => !EXCLUDE_COLUMNS.includes(key) && !key.startsWith('_'));
    
    // Remove duplicate location fields (prefer the name versions)
    if (allKeys.includes('division_name') && allKeys.includes('division')) {
      allKeys = allKeys.filter(key => key !== 'division');
    }
    if (allKeys.includes('district_name') && allKeys.includes('district')) {
      allKeys = allKeys.filter(key => key !== 'district');
    }
    if (allKeys.includes('ulb_name') && allKeys.includes('ulb')) {
      allKeys = allKeys.filter(key => key !== 'ulb');
    }

    // Sort columns: location names first, then alphabetically
    const locationFields = ['division_name', 'district_name', 'ulb_name'];
    const locationCols = allKeys.filter(key => locationFields.includes(key));
    const otherCols = allKeys.filter(key => !locationFields.includes(key)).sort();

    const finalColumns = [...locationCols, ...otherCols];

    console.log("🔍 [DEBUG] getAllColumns - FINAL COLUMN COUNT:", finalColumns.length);

    // Create table columns - simpler version that works well in modal
    const tableColumns = finalColumns.map(key => ({
      title: (
        <Tooltip title={key}>
          <span>{getFieldDisplayName(key)}</span>
        </Tooltip>
      ),
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150,
      sorter: (a, b) => {
        const valA = a[key] || '';
        const valB = b[key] || '';
        return String(valA).localeCompare(String(valB));
      },
      render: (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') return value.toLocaleString();
        if (typeof value === 'string' && value.length > 50) {
          return <Tooltip title={value}>{value.substring(0, 47)}...</Tooltip>;
        }
        return value;
      }
    }));

    console.log("🔍 [DEBUG] getAllColumns - Table columns created:", tableColumns.length);

    return tableColumns;
  };

  // Build hierarchical columns - more detailed version
  const getHierarchicalColumns = () => {
    if (!formFieldsList.length) return getAllColumns();
    
    // This is a simplified version of the main component's renderHierarchicalColumns
    // For the modal, we'll use a simpler approach to ensure data shows up
    const excludeColumns = [
      'workflow_action', 'workflow_action_at', 'workflow_action_by',
      'workflow_action_by_name', 'workflow_action_by_role', 'workflow_action_comments',
      'workflow_sequence', '_workflow_status', 'submission_workflow_id',
      'workflow_instance_id', 'workflow_status', 'workflow_stage', 'workflow_updated_at',
      'progress', 'status', '_id', '_form_id', '_status', '_created_at', '_updated_at',
      'created_at', 'updated_at', 'division_id', 'district_id', 'ulb_id'
    ];

    const locationFieldNames = ['division_name', 'district_name', 'ulb_name', 'ulb_type', 'class'];
    const locationCols = locationFieldNames.filter(f => formFieldsList.includes(f) || filteredData[0]?.[f] !== undefined);
    
    const otherCols = formFieldsList
      .filter(f => !locationFieldNames.includes(f) && !excludeColumns.includes(f) && !f.includes('_id'))
      .sort();

    const allCols = [...locationCols, ...otherCols];

    return allCols.map(key => ({
      title: <Tooltip title={key}><span>{getFieldDisplayName(key)}</span></Tooltip>,
      dataIndex: key,
      key: key,
      ellipsis: true,
      width: 150,
      sorter: (a, b) => {
        const valA = a[key] || '';
        const valB = b[key] || '';
        return String(valA).localeCompare(String(valB));
      },
      render: (value) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'number') return value.toLocaleString();
        if (typeof value === 'string' && value.length > 50) {
          return <Tooltip title={value}>{value.substring(0, 47)}...</Tooltip>;
        }
        return value;
      }
    }));
  };

  // Get text field options for filtering
  const getTextFieldOptions = () => {
    if (!formFields || formFields.length === 0) return [];

    const excludeFields = [
      'id', '_id', 'created_at', 'updated_at', 'user_id', 'status',
      'division_id', 'district_id', 'ulb_id', 'population_id',
      'total_population', 'sc_population', 'st_population', 'area',
      'workflow_action', 'workflow_action_at', 'workflow_action_by',
      'workflow_action_by_name', 'workflow_action_by_role', 'workflow_action_comments',
      'workflow_sequence', '_workflow_status', 'submission_workflow_id',
      'workflow_instance_id', 'workflow_status', 'workflow_stage', 'workflow_updated_at',
      'progress', '_form_id', '_status', '_created_at', '_updated_at'
    ];

    return formFields.filter(field => {
      const fieldStr = String(field);
      const fieldLower = fieldStr.toLowerCase();

      if (fieldLower.endsWith('_id') || fieldLower === 'id') return false;
      if (excludeFields.includes(fieldLower)) return false;
      return true;
    });
  };

  // Build field hierarchy when form changes
  useEffect(() => {
    if (selectedForm) {
      buildFieldHierarchy(selectedForm.id);
      // Store form fields for column generation
      setFormFieldsList(formFields);
    }
  }, [selectedForm, formFields]);

  // Debug effect
  useEffect(() => {
    if (filteredData.length > 0) {
      console.log("📊 [DEBUG] filteredData updated - Records:", filteredData.length);
      const sampleRow = filteredData[0];
      console.log("📊 [DEBUG] Sample row keys after cleaning:", Object.keys(sampleRow));
    }
  }, [filteredData]);

  // Get active filter info
  const getActiveFilterInfo = () => {
    if (selectedField && selectedValue) {
      return {
        field: selectedField,
        fieldDisplayName: getFieldDisplayName(selectedField),
        value: selectedValue,
        recordCount: filteredData.length
      };
    }
    return null;
  };

  if (!selectedForm || getTextFieldOptions().length === 0) {
    return null;
  }

  const activeFilter = getActiveFilterInfo();
  const textFieldOptions = getTextFieldOptions();

  // Handle view all click - this is the key function that was missing
  const handleViewAllClick = () => {
    if (!filteredData.length) return;
    
    const allColumns = getHierarchicalColumns();
    
    console.log("🎯 [DEBUG] View All button clicked - Columns count:", allColumns.length);
    console.log("🎯 [DEBUG] Column names:", allColumns.map(c => c.dataIndex));
    console.log("🎯 [DEBUG] Filtered data rows:", filteredData.length);
    
    Modal.info({
      title: (
        <Space>
          <FilterOutlined />
          <span>Records matching "{selectedValue}"</span>
          <Tag color="blue">{filteredData.length} records</Tag>
          <Tag color="green">{allColumns.length} columns</Tag>
        </Space>
      ),
      width: "95%",
      style: { top: 20 },
      content: (
        <Table
          dataSource={filteredData}
          columns={allColumns}
          rowKey={(record, index) => `filtered_${index}`}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
          }}
          size="small"
          scroll={{ x: "max-content", y: 500 }}
        />
      ),
      okText: "Close",
      icon: null,
      maskClosable: true
    });
  };

  return (
    <Card
      title={
        <Space>
          <FilterOutlined style={{ color: "#1890ff" }} />
          <span style={{ fontWeight: 600 }}>Quick Filter by Field Value</span>
          <Tag color="blue" icon={<InfoCircleOutlined />}>
            Select Field → Choose Value → View Matching Rows
          </Tag>
        </Space>
      }
      style={{
        marginBottom: 16,
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0"
      }}
      bodyStyle={{ padding: "16px" }}
      size="small"
    >
      {/* Active Filter Banner */}
      {activeFilter && (
        <div style={{
          marginBottom: 16,
          padding: "8px 12px",
          background: "#e6f7ff",
          borderRadius: "8px",
          borderLeft: "3px solid #1890ff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px"
        }}>
          <Space>
            <FilterOutlined style={{ color: "#1890ff" }} />
            <span>
              <strong>Active Filter:</strong> {activeFilter.fieldDisplayName} = "{activeFilter.value}"
            </span>
            <Tag color={activeFilter.recordCount > 0 ? "blue" : "red"}>
              {activeFilter.recordCount} matching records
            </Tag>
          </Space>
          <Button 
            size="small" 
            type="link"
            onClick={handleClear}
            icon={<DeleteOutlined />}
          >
            Clear Filter
          </Button>
        </div>
      )}

      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: "11px" }}>
              <DatabaseOutlined /> 1. Select Field
            </Text>
          </div>
          <Select
            placeholder="Choose a field to filter by..."
            value={selectedField}
            onChange={handleFieldSelect}
            style={{ width: "100%" }}
            showSearch
            allowClear
            size="middle"
            dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
            notFoundContent="No fields available"
          >
            {textFieldOptions.map(field => (
              <Option key={field} value={field}>
                <Space>
                  <span>📋</span>
                  <span>{getFieldDisplayName(field)}</span>
                  <span style={{ fontSize: "10px", color: "#8c8c8c" }}>
                    ({field})
                  </span>
                </Space>
              </Option>
            ))}
          </Select>
        </Col>

        {selectedField && (
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 6 }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>
                <SearchOutlined /> 2. Select Value
              </Text>
            </div>
            <Select
              placeholder={loadingValues ? "Loading unique values..." : "Choose a value to filter..."}
              value={selectedValue}
              onChange={handleValueSelect}
              style={{ width: "100%" }}
              showSearch
              allowClear
              loading={loadingValues}
              size="middle"
              dropdownStyle={{ backgroundColor: "#ffffff", zIndex: 1050 }}
              notFoundContent={loadingValues ? <Spin size="small" /> : (uniqueValues.length === 0 ? "No values available" : "No matching values")}
            >
              {uniqueValues.map(value => (
                <Option key={value} value={value}>
                  <Tooltip title={value}>
                    {value.length > 60 ? value.substring(0, 57) + "..." : value}
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </Col>
        )}

        {(selectedField || selectedValue || showResults) && (
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 6 }}>&nbsp;</div>
            <Space style={{ width: "100%" }}>
              <Button 
                icon={<DeleteOutlined />} 
                onClick={handleClear}
                danger
                size="middle"
                style={{ flex: 1 }}
              >
                Clear All
              </Button>
              {showResults && filteredData.length > 0 && (
                <Button 
                  type="primary"
                  icon={<EyeOutlined />}
                  onClick={handleViewAllClick}
                  style={{ flex: 1 }}
                >
                  View All ({filteredData.length})
                </Button>
              )}
            </Space>
          </Col>
        )}
      </Row>

      {showResults && selectedValue && (filteredData.length > 0 ? (
        <div style={{ marginTop: 16 }}>
          <Divider style={{ margin: "8px 0" }} />
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Badge 
                count={filteredData.length} 
                showZero 
                style={{ backgroundColor: filteredData.length > 0 ? "#1890ff" : "#8c8c8c" }}
              />
              <Text strong>
                Records matching "{selectedValue}"
              </Text>
              <Tag color="geekblue" icon={<FilterOutlined />}>
                Field: {getFieldDisplayName(selectedField)}
              </Tag>
            </Space>
          </div>

          <Table
            dataSource={filteredData.slice(0, 5)}
            columns={getAllColumns().slice(0, 8)}
            rowKey={(record, index) => `preview_${index}`}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            bordered
          />
          {filteredData.length > 5 && (
            <div style={{ textAlign: "center", marginTop: 12 }}>
              <Text type="secondary">+ {filteredData.length - 5} more records. Click "View All" to see all columns.</Text>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 16, textAlign: "center", padding: "20px" }}>
          <Alert
            message="No Matching Records"
            description={`No records found where ${getFieldDisplayName(selectedField)} equals "${selectedValue}".`}
            type="warning"
            showIcon
          />
        </div>
      ))}
    </Card>
  );
};

// ==================== MAIN FORM SELECTION VIEW COMPONENT ====================
const FormSelectionView = ({ form, onBack, referenceCache: externalRefCache }) => {
  // State declarations (keep all your existing state)
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState([]);
  const [originalFormData, setOriginalFormData] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [fieldLabels, setFieldLabels] = useState({});
  const [reportEnabledFields, setReportEnabledFields] = useState([]);
  const [reportCalculations, setReportCalculations] = useState({});
  const [loadingReportFields, setLoadingReportFields] = useState(false);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [error, setError] = useState(null);
  const [fieldGraphsData, setFieldGraphsData] = useState({});
  const [loadingGraphs, setLoadingGraphs] = useState({});
  const [graphModalVisible, setGraphModalVisible] = useState(false);
  const [graphModalData, setGraphModalData] = useState(null);
  const [graphModalLoading, setGraphModalLoading] = useState(false);
  const [formColumnSearchTerm, setFormColumnSearchTerm] = useState("");
  const [searchingFormColumns, setSearchingFormColumns] = useState(false);
  const [filteredReportFields, setFilteredReportFields] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userDistrictId, setUserDistrictId] = useState(null);
  const [userDivisionId, setUserDivisionId] = useState(null);
  const [userUlbId, setUserUlbId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [filters, setFilters] = useState({
    division: null,
    district: null,
    ulb_name: null,
    ulb_type: null,
    ulb_class: null,
  });
  const [availableFilters, setAvailableFilters] = useState({
    districts: [],
    divisions: [],
    ulbs: [],
  });
  const [ulbTypes, setUlbTypes] = useState([]);
  const [ulbClasses, setUlbClasses] = useState([]);
  const [selectedDataPointId, setSelectedDataPointId] = useState(null);
  const [viewMode, setViewMode] = useState("single");
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [dataModalPagination, setDataModalPagination] = useState({ current: 1, pageSize: 10 });
  const [modalFilters, setModalFilters] = useState({
    division: null,
    district: null,
    ulb: null,
    ulb_type: null,
    ulb_class: null,
    searchText: "",
  });
  const [referenceCache, setReferenceCache] = useState(externalRefCache || {
    districts: {},
    divisions: {},
    ulbs: {}
  });
  const [quickFilter, setQuickFilter] = useState({
    field: null,
    value: null,
    filteredData: [],
    isActive: false,
    fieldDisplayName: null
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const containerHeight = "calc(100vh - 280px)";

  // ==================== ROLE-BASED HELPER FUNCTIONS ====================
  
  const hasFullAccess = () => {
    if (!userRole) return false;
    const fullAccessRoles = [
      "SUPER_ADMIN", "super_admin", "ADMIN", 
      "DMA_DESK_OFFICER", "DMA_USER", "dma_user"
    ];
    return fullAccessRoles.includes(userRole);
  };

  const getUserDivisionName = () => {
    if (userDivisionId && referenceCache.divisions?.[userDivisionId]) {
      return referenceCache.divisions[userDivisionId].name;
    }
    return null;
  };

  const getUserDistrictName = () => {
    if (userDistrictId && referenceCache.districts?.[userDistrictId]) {
      return referenceCache.districts[userDistrictId].name;
    }
    return null;
  };

  const getUserUlbName = () => {
    if (userUlbId && referenceCache.ulbs?.[userUlbId]) {
      return referenceCache.ulbs[userUlbId].ulb_name;
    }
    return null;
  };

  const isDivisionAdmin = !hasFullAccess() && (
    userRole === "DIVISION_ADMIN" || userRole === "division_admin" ||
    userRole === "DIVISIONAL_OFFICER" || userRole === "divisional_officer" ||
    userRole === "Divisionalcommissioner"
  );

  const isDistrictAdmin = !hasFullAccess() && (
    userRole === "DISTRICT_ADMIN" || userRole === "district_admin"
  );

  const isUlbAdmin = !hasFullAccess() && (
    userRole === "ULB_ADMIN" || userRole === "ulb_admin" ||
    userRole === "ULB_OFFICER" || userRole === "ULB_CHIEF_OFFICIER"
  );

  const getRoleBasedFilters = () => {
    const forcedFilters = {};
    if (isDivisionAdmin) {
      const divisionName = getUserDivisionName();
      if (divisionName) forcedFilters.division = divisionName;
    }
    if (isDistrictAdmin) {
      const districtName = getUserDistrictName();
      if (districtName) forcedFilters.district = districtName;
      const divisionName = getUserDivisionName();
      if (divisionName && !forcedFilters.division) forcedFilters.division = divisionName;
    }
    if (isUlbAdmin) {
      const ulbName = getUserUlbName();
      if (ulbName) forcedFilters.ulb_name = ulbName;
      const districtName = getUserDistrictName();
      if (districtName && !forcedFilters.district) forcedFilters.district = districtName;
      const divisionName = getUserDivisionName();
      if (divisionName && !forcedFilters.division) forcedFilters.division = divisionName;
    }
    return forcedFilters;
  };

  const getCurrentUser = () => {
    try {
      const storedUser = sessionStorage.getItem("user");
      if (storedUser) {
        const user = JSON.parse(storedUser);
        setCurrentUser(user);
        setUserRole(user.role_name || user.role || user.userRole);
        setUserDivisionId(user.division_id || user.divisionId || user.division);
        setUserDistrictId(user.district_id || user.districtId || user.district);
        setUserUlbId(user.ulb_id || user.ulbId || user.ulb);
      }
    } catch (error) {
      console.error("Error loading user:", error);
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    getCurrentUser();
  }, []);

  // ==================== API CALLS ====================
  
  const fetchFormFieldLabels = async (formId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/structure`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      if (response.data.success && response.data.fields) {
        const fieldMap = {};
        response.data.fields.forEach((field) => {
          fieldMap[field.id] = field.label || field.id;
        });
        return fieldMap;
      }
      return {};
    } catch (error) {
      console.error("Error fetching field labels:", error);
      return {};
    }
  };

  const fetchReportEnabledFields = async (formId) => {
    try {
      setLoadingReportFields(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/schema?report_enabled=true`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      if (response.data.success && response.data.reportEnabledFields) {
        setReportEnabledFields(response.data.reportEnabledFields);
        setFilteredReportFields(response.data.reportEnabledFields);
        return response.data.reportEnabledFields;
      }
      return [];
    } catch (error) {
      console.error("Error fetching report-enabled fields:", error);
      return [];
    } finally {
      setLoadingReportFields(false);
    }
  };

  const processReportCalculations = (data, reportFields) => {
    if (!data || data.length === 0 || !reportFields || reportFields.length === 0) return {};

    const calculations = {};
    reportFields.forEach((field) => {
      const actualColumnName = field.column_name || field.field_id;
      const operator = field.operator || "sum";

      const values = data
        .map((row) => {
          const val = row[actualColumnName];
          if (val === undefined || val === null || val === "") return null;
          const strVal = String(val).replace(/,/g, "");
          const numVal = parseFloat(strVal);
          return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
        })
        .filter((v) => v !== null);

      let result = 0;
      if (values.length > 0) {
        switch (operator.toLowerCase()) {
          case "sum":
            result = values.reduce((a, b) => a + b, 0);
            break;
          case "avg":
          case "average":
            result = values.reduce((a, b) => a + b, 0) / values.length;
            break;
          case "min":
            result = Math.min(...values);
            break;
          case "max":
            result = Math.max(...values);
            break;
          case "count":
            result = values.length;
            break;
          default:
            result = values.reduce((a, b) => a + b, 0);
        }
      }

      calculations[field.field_id] = {
        value: result,
        operator,
        count: values.length,
        display_name: field.display_name || field.field_id,
      };
    });
    return calculations;
  };

  const detectNumericColumnsFromData = (data) => {
    if (!data || data.length === 0) return [];
    const numericColumns = [];
    const sampleSize = Math.min(data.length, 50);
    const fields = Object.keys(data[0] || {}).filter(
      (key) => !key.startsWith("_") && !key.endsWith("_formatted") && !key.endsWith("_numeric") &&
        key !== "id" && key !== "created_at" && key !== "updated_at"
    );

    fields.forEach((field) => {
      let numericCount = 0;
      let totalChecked = 0;
      for (let i = 0; i < sampleSize; i++) {
        const value = data[i][field];
        if (value !== null && value !== undefined && value !== "") {
          totalChecked++;
          const strValue = String(value).replace(/,/g, "");
          const numValue = parseFloat(strValue);
          if (!isNaN(numValue) && isFinite(numValue)) numericCount++;
        }
      }
      if (totalChecked > 0 && numericCount / totalChecked >= 0.8) {
        numericColumns.push({
          field: field,
          displayName: fieldLabels[field] || formatFieldName(field),
        });
      }
    });
    return numericColumns;
  };

  const loadFilterOptions = async (formId) => {
    try {
      const params = new URLSearchParams();
      const roleFilters = getRoleBasedFilters();
      if (roleFilters.division) params.set("division", roleFilters.division);
      if (roleFilters.district) params.set("district", roleFilters.district);
      if (roleFilters.ulb_name) params.set("ulb_name", roleFilters.ulb_name);
      if (filters.division && !roleFilters.division) params.set("division", filters.division);
      if (filters.district && !roleFilters.district) params.set("district", filters.district);
      if (filters.ulb_type) params.set("ulb_type", filters.ulb_type);
      if (filters.ulb_class) params.set("ulb_class", filters.ulb_class);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/filter-options?${params}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );

      if (response.data.success) {
        setAvailableFilters({
          districts: response.data.districts || [],
          divisions: response.data.divisions || [],
          ulbs: response.data.ulbs || [],
        });
        setUlbTypes(response.data.ulbTypes || []);
        setUlbClasses(response.data.ulbClasses || []);
      }
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  const loadFormData = async (formId, currentFilters = {}) => {
    try {
      setLoadingFormData(true);
      setError(null);

      const params = new URLSearchParams({ limit: "10000", all_fields: "true" });
      const roleFilters = getRoleBasedFilters();
      if (roleFilters.division) params.set("division", roleFilters.division);
      if (roleFilters.district) params.set("district", roleFilters.district);
      if (roleFilters.ulb_name) params.set("ulb_name", roleFilters.ulb_name);
      if (currentFilters.district && !roleFilters.district) params.set("district", currentFilters.district);
      if (currentFilters.division && !roleFilters.division) params.set("division", currentFilters.division);
      if (currentFilters.ulb_name && !roleFilters.ulb_name) params.set("ulb_name", currentFilters.ulb_name);
      if (currentFilters.ulb_type) params.set("ulb_type", currentFilters.ulb_type);
      if (currentFilters.ulb_class) params.set("ulb_class", currentFilters.ulb_class);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${formId}/data?${params}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );

      if (response.data.success) {
        let data = response.data.data || [];
        data = resolveIdsInData(data, referenceCache);
        const approvedData = getApprovedSubmissionsOnly(formId, data);
        
        if (approvedData.length === 0) {
          notification.info({ message: "No Approved Data", description: "This form has no approved submissions." });
          setFormData([]);
          setOriginalFormData([]);
          return;
        }

        setFormData(approvedData);
        if (originalFormData.length === 0) {
          setOriginalFormData([...approvedData]);
        }

        detectNumericColumnsFromData(approvedData);

        let fieldNames = [];
        if (response.data.fields && Array.isArray(response.data.fields)) {
          fieldNames = response.data.fields.map((field) => typeof field === "string" ? field : field.id || String(field));
        }
        if (fieldNames.length === 0 && data.length > 0) {
          const allKeys = new Set();
          data.forEach((row) => {
            Object.keys(row).forEach((key) => {
              if (!key.startsWith("_") && key !== "_id" && key !== "_form_id") allKeys.add(key);
            });
          });
          fieldNames = Array.from(allKeys);
        }
        setFormFields(fieldNames);

        const reportFields = await fetchReportEnabledFields(formId);
        if (reportFields.length > 0) {
          const calculations = processReportCalculations(approvedData, reportFields);
          setReportCalculations(calculations);
        }
      }
    } catch (error) {
      console.error("Error loading form data:", error);
      setError("Failed to load form data");
    } finally {
      setLoadingFormData(false);
    }
  };

  const generateColumnGraph = async (columnName, displayName, fieldId, chartType = "bar") => {
    if (!form) return null;
    const graphKey = fieldId || columnName;
    try {
      setLoadingGraphs((prev) => ({ ...prev, [graphKey]: true }));
      const params = new URLSearchParams({ limit: "10000", all_fields: "true" });
      const roleFilters = getRoleBasedFilters();
      if (roleFilters.division) params.set("division", roleFilters.division);
      if (roleFilters.district) params.set("district", roleFilters.district);
      if (roleFilters.ulb_name) params.set("ulb_name", roleFilters.ulb_name);
      if (filters.division && !roleFilters.division) params.set("division", filters.division);
      if (filters.district && !roleFilters.district) params.set("district", filters.district);
      if (filters.ulb_name && !roleFilters.ulb_name) params.set("ulb_name", filters.ulb_name);
      if (filters.ulb_type) params.set("ulb_type", filters.ulb_type);
      if (filters.ulb_class) params.set("ulb_class", filters.ulb_class);

      const response = await axios.get(
        `${API_BASE_URL}/api/reports/forms/${form.id}/data?${params}`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );

      if (response.data.success && response.data.data) {
        let data = response.data.data;
        data = resolveIdsInData(data, referenceCache);
        let filteredData = getApprovedSubmissionsOnly(form.id, data);
        if (roleFilters.division) filteredData = filteredData.filter(row => (row.division_name || row.division) === roleFilters.division);
        if (roleFilters.district) filteredData = filteredData.filter(row => (row.district_name || row.district) === roleFilters.district);
        if (roleFilters.ulb_name) filteredData = filteredData.filter(row => (row.ulb_name || row.ulb) === roleFilters.ulb_name);
        if (filters.division && !roleFilters.division) filteredData = filteredData.filter(row => (row.division_name || row.division) === filters.division);
        if (filters.district && !roleFilters.district) filteredData = filteredData.filter(row => (row.district_name || row.district) === filters.district);
        if (filters.ulb_type) filteredData = filteredData.filter(row => row.ulb_type === filters.ulb_type);
        if (filters.ulb_class) filteredData = filteredData.filter(row => row.class === filters.ulb_class);
        if (filters.ulb_name && !roleFilters.ulb_name) filteredData = filteredData.filter(row => (row.ulb_name || row.ulb) === filters.ulb_name);

        let groupByField = "division_name";
        let groupLabel = "Division";
        let aggregationLevel = "division";
        if (filters.ulb_name || roleFilters.ulb_name) {
          groupByField = null;
          groupLabel = "Record";
          aggregationLevel = "record";
        } else if (filters.district || roleFilters.district) {
          groupByField = "ulb_name";
          groupLabel = "ULB";
          aggregationLevel = "ulb";
        } else if (filters.division || roleFilters.division) {
          groupByField = "district_name";
          groupLabel = "District";
          aggregationLevel = "district";
        }

        let chartData = [];
        let totalSum = 0;
        if (groupByField && aggregationLevel !== "record") {
          const groupedData = {};
          filteredData.forEach((row) => {
            let groupValue = row[groupByField] || `Unknown ${groupLabel}`;
            const value = parseFloat(String(row[columnName] || "").replace(/,/g, ""));
            if (!isNaN(value) && isFinite(value)) {
              if (!groupedData[groupValue]) groupedData[groupValue] = { name: groupValue, value: 0, count: 0, originalName: groupValue };
              groupedData[groupValue].value += value;
              groupedData[groupValue].count++;
            }
          });
          chartData = Object.values(groupedData).map(item => ({ name: item.name, value: item.value, count: item.count, avgValue: item.value / item.count })).sort((a, b) => a.name.localeCompare(b.name));
          totalSum = chartData.reduce((sum, item) => sum + item.value, 0);
        } else {
          chartData = filteredData.map((row, index) => {
            const value = parseFloat(String(row[columnName] || "").replace(/,/g, ""));
            if (!isNaN(value) && isFinite(value)) return { name: `Record ${index + 1}`, value: value, originalName: `Record ${index + 1}` };
            return null;
          }).filter(v => v !== null);
          totalSum = chartData.reduce((sum, item) => sum + item.value, 0);
        }

        const generatedGraph = {
          column: columnName,
          displayName: displayName,
          data: chartData,
          total: totalSum,
          aggregationLevel: aggregationLevel,
          groupLabel: groupLabel,
          showGraph: true,
          isGraphReady: true,
          chartType: chartType,
        };
        setFieldGraphsData(prev => ({ ...prev, [graphKey]: generatedGraph }));
        return generatedGraph;
      }
    } catch (error) {
      console.error("Error generating graph:", error);
    } finally {
      setLoadingGraphs(prev => ({ ...prev, [graphKey]: false }));
    }
    return null;
  };

  const toggleChartType = (graphKey) => {
    setFieldGraphsData(prev => {
      const current = prev[graphKey];
      if (!current) return prev;
      return { ...prev, [graphKey]: { ...current, chartType: current.chartType === "bar" ? "pie" : "bar" } };
    });
  };

  // ==================== FILTER HANDLERS ====================
  const handleDivisionChange = async (value) => {
    const roleFilters = getRoleBasedFilters();
    if (roleFilters.division) return;
    const newFilters = { ...filters, division: value, district: null, ulb_name: null };
    setFilters(newFilters);
    if (value) {
      await loadFilterOptions(form.id);
      await loadFormData(form.id, newFilters);
    } else {
      resetFilters();
    }
  };

  const handleDistrictChange = async (value) => {
    const roleFilters = getRoleBasedFilters();
    if (roleFilters.district) return;
    const newFilters = { ...filters, district: value, ulb_name: null };
    setFilters(newFilters);
    if (value) {
      await loadFilterOptions(form.id);
      await loadFormData(form.id, newFilters);
    } else if (filters.division) {
      await loadFilterOptions(form.id);
      await loadFormData(form.id, { ...filters, district: null });
    } else {
      resetFilters();
    }
  };

  const handleUlbChange = async (value) => {
    const roleFilters = getRoleBasedFilters();
    if (roleFilters.ulb_name) return;
    const newFilters = { ...filters, ulb_name: value };
    setFilters(newFilters);
    await loadFormData(form.id, newFilters);
  };

  const handleUlbTypeChange = async (value) => {
    const newFilters = { ...filters, ulb_type: value };
    setFilters(newFilters);
    await loadFilterOptions(form.id);
    await loadFormData(form.id, newFilters);
  };

  const handleUlbClassChange = async (value) => {
    const newFilters = { ...filters, ulb_class: value };
    setFilters(newFilters);
    await loadFilterOptions(form.id);
    await loadFormData(form.id, newFilters);
  };

  const resetFilters = () => {
    const roleFilters = getRoleBasedFilters();
    const newFilters = { 
      division: roleFilters.division || null, 
      district: roleFilters.district || null, 
      ulb_name: roleFilters.ulb_name || null, 
      ulb_type: null, 
      ulb_class: null 
    };
    setFilters(newFilters);
    loadFilterOptions(form.id);
    loadFormData(form.id, newFilters);
    message.info("Filters reset");
  };

  const applyFilters = () => {
    loadFormData(form.id, filters);
    message.success("Filters applied");
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderFilterSection = () => {
    const divisionValues = availableFilters.divisions || [];
    const districtValues = availableFilters.districts || [];
    const ulbValues = availableFilters.ulbs || [];
    const roleFilters = getRoleBasedFilters();
    const displayDivisionValue = roleFilters.division || filters.division;
    const displayDistrictValue = roleFilters.district || filters.district;
    const displayUlbValue = roleFilters.ulb_name || filters.ulb_name;
    const isDivisionDisabled = isDivisionAdmin || isDistrictAdmin || isUlbAdmin;
    const isDistrictDisabled = isDistrictAdmin || isUlbAdmin;
    const isUlbDisabled = isUlbAdmin;

    return (
      <Card
        style={{
          marginBottom: 16,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: "16px" }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
          {ulbTypes.length > 0 && (
            <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>ULB Type</Text>
              <Select placeholder="All Types" value={filters.ulb_type} onChange={handleUlbTypeChange} allowClear style={{ width: "100%" }} showSearch size="middle">
                <Option value="">All Types</Option>
                {ulbTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
              </Select>
            </div>
          )}
          {ulbClasses.length > 0 && (
            <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>ULB Class</Text>
              <Select placeholder="All Classes" value={filters.ulb_class} onChange={handleUlbClassChange} allowClear style={{ width: "100%" }} showSearch size="middle">
                <Option value="">All Classes</Option>
                {ulbClasses.map(cls => <Option key={cls} value={cls}>{cls}</Option>)}
              </Select>
            </div>
          )}
          {divisionValues.length > 0 && (
            <div style={{ flex: "1 1 180px", minWidth: "180px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>Division</Text>
              <Select placeholder={isDivisionDisabled ? getUserDivisionName() || "Division" : "All Divisions"} value={displayDivisionValue} onChange={handleDivisionChange} allowClear={!isDivisionDisabled} style={{ width: "100%" }} showSearch size="middle" disabled={isDivisionDisabled}>
                <Option value="">All Divisions</Option>
                {divisionValues.map(div => <Option key={div} value={div}>{div}</Option>)}
              </Select>
            </div>
          )}
          {districtValues.length > 0 && (
            <div style={{ flex: "1 1 180px", minWidth: "180px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>District</Text>
              <Select placeholder={isDistrictDisabled ? getUserDistrictName() || "District" : (filters.division ? "Select District" : "Select Division first")} value={displayDistrictValue} onChange={handleDistrictChange} allowClear={!isDistrictDisabled} style={{ width: "100%" }} showSearch size="middle" disabled={isDistrictDisabled || (!filters.division && !roleFilters.division && divisionValues.length > 0)}>
                <Option value="">All Districts</Option>
                {districtValues.map(dist => <Option key={dist} value={dist}>{dist}</Option>)}
              </Select>
            </div>
          )}
          {ulbValues.length > 0 && (
            <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
              <Text type="secondary" style={{ fontSize: "11px" }}>ULB</Text>
              <Select placeholder={isUlbDisabled ? getUserUlbName() || "ULB" : (filters.district ? "Select ULB" : filters.division ? "Select District first" : "Select filters first")} value={displayUlbValue} onChange={handleUlbChange} allowClear={!isUlbDisabled} style={{ width: "100%" }} showSearch size="middle" disabled={isUlbDisabled || (!filters.district && !roleFilters.district && !filters.division && !roleFilters.division)}>
                <Option value="">All ULBs</Option>
                {ulbValues.map(ulb => <Option key={ulb} value={ulb}>{ulb}</Option>)}
              </Select>
            </div>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            <Button type="primary" icon={<FilterOutlined />} onClick={applyFilters} loading={loadingFormData}>Apply</Button>
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderInlineGraph = (graphKey, graphData) => {
    if (!graphData || !graphData.data || graphData.data.length === 0) return null;
    const { data, displayName, aggregationLevel, groupLabel, chartType = "bar", total } = graphData;
    const isPieChart = chartType === "pie";
    const totalValue = total || data.reduce((sum, item) => sum + item.value, 0);
    const pieData = data.slice(0, 12).map(item => ({ name: item.name.length > 20 ? item.name.substring(0, 17) + "..." : item.name, fullName: item.name, value: item.value }));

    return (
      <div style={{ marginTop: "16px", padding: "12px", background: "#fafafa", borderRadius: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
          <div><Text strong style={{ fontSize: "12px", color: "#1890ff" }}><BarChartOutlined /> {displayName} by {groupLabel || "Location"}</Text></div>
          <Space size={4}>
            <Tooltip title="Bar Chart"><Button size="small" type={!isPieChart ? "primary" : "default"} icon={<BarChartOutlined />} onClick={() => toggleChartType(graphKey)} /></Tooltip>
            <Tooltip title="Pie Chart"><Button size="small" type={isPieChart ? "primary" : "default"} icon={<PieChartOutlined />} onClick={() => toggleChartType(graphKey)} /></Tooltip>
            <Tooltip title="Full Screen"><Button size="small" icon={<FullscreenOutlined />} onClick={() => { setGraphModalData(graphData); setGraphModalVisible(true); }} /></Tooltip>
          </Space>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          {isPieChart ? (
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => percent > 0.05 ? `${name} (${(percent * 100).toFixed(0)}%)` : ""} outerRadius={80} innerRadius={40} dataKey="value" paddingAngle={2}>
                {pieData.map((_, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
              </Pie>
              <RechartsTooltip content={({ active, payload }) => {
                if (active && payload?.length) {
                  const item = payload[0].payload;
                  const percentage = ((item.value / totalValue) * 100).toFixed(1);
                  return <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}><strong>{item.fullName || item.name}</strong><br />Value: {item.value.toLocaleString()}<br />Percentage: {percentage}%</div>;
                }
                return null;
              }} />
            </PieChart>
          ) : (
            <BarChart data={data} margin={{ top: 10, right: 10, left: 30, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={70} interval={0} tick={{ fontSize: 9 }} />
              <YAxis tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()} />
              <RechartsTooltip content={({ active, payload }) => {
                if (active && payload?.length) {
                  const item = payload[0].payload;
                  return <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px" }}><strong>{item.name}</strong><br />Value: {item.value.toLocaleString()}</div>;
                }
                return null;
              }} />
              <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
        <div style={{ marginTop: "8px", fontSize: "10px", color: "#8c8c8c", textAlign: "center" }}>Total: {totalValue.toLocaleString()} • {data.length} items</div>
      </div>
    );
  };

  const renderReportCards = () => {
    if (!reportCalculations || Object.keys(reportCalculations).length === 0) return <Empty description="No data points available" />;
    const fieldsToShow = selectedDataPointId ? reportEnabledFields.filter(f => f.field_id === selectedDataPointId) : reportEnabledFields;
    if (fieldsToShow.length === 0) return <Empty description="No data points match the filter" />;

    const formatValue = (value, operator) => {
      if (value === undefined || value === null) return "0";
      if (operator?.toLowerCase() === "percentage") return `${value.toFixed(2)}%`;
      if (operator?.toLowerCase() === "avg" || operator?.toLowerCase() === "average") return value.toFixed(2);
      return value.toLocaleString();
    };

    const getOperatorColor = (operator) => {
      const colors = { sum: "#1890ff", avg: "#52c41a", min: "#fa8c16", max: "#f5222d", count: "#722ed1", percentage: "#eb2f96" };
      return colors[operator?.toLowerCase()] || "#1890ff";
    };

    const calculateStateTotal = (fieldId, calculation) => {
      const fieldInfo = reportEnabledFields.find(f => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0 ? quickFilter.filteredData : originalFormData;
      const values = dataToUse.map(row => {
        const val = row[actualColumnName];
        if (val === undefined || val === null) return null;
        const numVal = parseFloat(String(val).replace(/,/g, ""));
        return !isNaN(numVal) ? numVal : null;
      }).filter(v => v !== null);
      if (values.length === 0) return "0";
      switch (operator.toLowerCase()) {
        case "sum": return values.reduce((a, b) => a + b, 0).toLocaleString();
        case "avg": return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
        case "min": return Math.min(...values).toLocaleString();
        case "max": return Math.max(...values).toLocaleString();
        case "count": return values.length.toLocaleString();
        default: return values.reduce((a, b) => a + b, 0).toLocaleString();
      }
    };

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ marginBottom: "12px", display: "flex", justifyContent: "flex-end" }}>
          <Space><span style={{ fontSize: "12px" }}>View Mode:</span><Switch checkedChildren="2 Cards" unCheckedChildren="1 Card" checked={viewMode === "double"} onChange={(checked) => setViewMode(checked ? "double" : "single")} /></Space>
        </div>
        <div style={{ flex: 1, overflowY: "auto", paddingRight: "4px" }}>
          <div style={{ display: viewMode === "double" ? "grid" : "flex", gridTemplateColumns: viewMode === "double" ? "repeat(2, 1fr)" : "none", flexDirection: viewMode === "double" ? "row" : "column", gap: "16px" }}>
            {fieldsToShow.map((field, idx) => {
              const calculation = reportCalculations[field.field_id];
              if (!calculation) return null;
              const displayName = field.display_name || calculation.display_name || formatFieldName(field.field_id);
              const operator = calculation.operator || "sum";
              const operatorColor = getOperatorColor(operator);
              const graphKey = field.field_id;
              const graphData = fieldGraphsData[graphKey];
              const isLoadingGraph = loadingGraphs[graphKey];
              if (!graphData && !isLoadingGraph) setTimeout(() => generateColumnGraph(field.column_name || field.field_id, displayName, field.field_id, "pie"), 100);
              return (
                <div key={field.field_id} style={{ padding: "16px", border: `1px solid ${operatorColor}20`, borderRadius: "12px", background: "#fff", position: "relative", boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: operatorColor, borderRadius: "3px 3px 0 0" }} />
                  <div style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
                      <div>
                        <Text strong style={{ fontSize: "15px" }}>{displayName}</Text>
                        <div style={{ marginTop: "8px", padding: "6px 12px", background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5", borderRadius: "8px", borderLeft: quickFilter.isActive ? "3px solid #1890ff" : "none" }}>
                          <div style={{ fontSize: "11px", color: quickFilter.isActive ? "#1890ff" : "#666" }}>{quickFilter.isActive ? `Filtered Total (${quickFilter.fieldDisplayName} = ${quickFilter.value})` : "State Total"}</div>
                          <div style={{ fontSize: "18px", fontWeight: 700, color: "#1890ff" }}>{calculateStateTotal(field.field_id, calculation)}</div>
                        </div>
                      </div>
                      <Button type="primary" size="small" icon={<EyeOutlined />} onClick={() => setDataModalVisible(true)}>View All Data</Button>
                    </div>
                  </div>
                  {viewMode === "single" && graphData && graphData.isGraphReady && graphData.data?.length > 0 && renderInlineGraph(graphKey, graphData)}
                  {viewMode === "single" && isLoadingGraph && <div style={{ textAlign: "center", padding: "40px" }}><Spin /></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderDataModal = () => {
    if (!formData.length) return null;
    const paginatedData = formData.slice((dataModalPagination.current - 1) * dataModalPagination.pageSize, dataModalPagination.current * dataModalPagination.pageSize);
    const columns = Object.keys(formData[0] || {}).slice(0, 10).map(key => ({ title: formatFieldName(key), dataIndex: key, key: key, ellipsis: true, width: 150, render: (value) => value === null || value === undefined ? '-' : String(value) }));
    return (
      <Modal title={`${form.displayName} - Data Records`} open={dataModalVisible} onCancel={() => setDataModalVisible(false)} footer={null} width="90%" style={{ top: 20 }} bodyStyle={{ padding: "16px" }}>
        <Table dataSource={paginatedData} columns={columns} rowKey={(_, index) => index} pagination={{ current: dataModalPagination.current, pageSize: dataModalPagination.pageSize, total: formData.length, showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`, onChange: (page, pageSize) => setDataModalPagination({ current: page, pageSize }) }} size="small" scroll={{ x: "max-content", y: 500 }} />
      </Modal>
    );
  };

  const renderGraphModal = () => {
    if (!graphModalData) return null;
    const { displayName, data, chartType = "bar", total } = graphModalData;
    const isPieChart = chartType === "pie";
    const totalValue = total || data?.reduce((sum, item) => sum + item.value, 0) || 0;
    return (
      <Modal title={displayName} open={graphModalVisible} onCancel={() => { setGraphModalVisible(false); setGraphModalData(null); }} footer={null} width={800} bodyStyle={{ padding: "20px" }}>
        <ResponsiveContainer width="100%" height={400}>
          {isPieChart ? (
            <PieChart>
              <Pie data={data?.slice(0, 12)} cx="50%" cy="50%" labelLine={true} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`} outerRadius={130} innerRadius={60} dataKey="value" paddingAngle={2}>
                {data?.slice(0, 12).map((_, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
              </Pie>
              <RechartsTooltip content={({ active, payload }) => {
                if (active && payload?.length) {
                  const item = payload[0].payload;
                  const percentage = ((item.value / totalValue) * 100).toFixed(1);
                  return <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px" }}><strong>{item.name}</strong><br />Value: {item.value.toLocaleString()}<br />Percentage: {percentage}%</div>;
                }
                return null;
              }} />
            </PieChart>
          ) : (
            <BarChart data={data} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()} />
              <RechartsTooltip />
              <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Modal>
    );
  };

  // Load data on mount
  useEffect(() => {
    if (form) {
      const init = async () => {
        setLoading(true);
        const labels = await fetchFormFieldLabels(form.id);
        setFieldLabels(labels);
        await loadFilterOptions(form.id);
        await loadFormData(form.id);
        setLoading(false);
      };
      init();
    }
  }, [form]);

  if (!form) return null;

  const renderReportEnabledCardsWithFilter = (fieldsToShowParam = null) => {
    const fieldsToShow = fieldsToShowParam || (formColumnSearchTerm.trim() ? filteredReportFields : reportEnabledFields);
    if (!reportCalculations || Object.keys(reportCalculations).length === 0) return null;
    if (fieldsToShow.length === 0) {
      return (
        <Card style={{ marginBottom: 24, borderRadius: "12px", background: "#fafafa", textAlign: "center", padding: "40px" }}>
          <Empty description={formColumnSearchTerm ? `No data points found matching "${formColumnSearchTerm}"` : "No report-enabled fields available"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      );
    }

    const filteredCalculations = {};
    fieldsToShow.forEach((field) => { if (reportCalculations[field.field_id]) filteredCalculations[field.field_id] = reportCalculations[field.field_id]; });
    const allFields = Object.entries(filteredCalculations);
    const sortedAllFields = [...allFields].sort((a, b) => {
      const [fieldIdA, calculationA] = a;
      const [fieldIdB, calculationB] = b;
      const fieldInfoA = fieldsToShow.find((f) => f.field_id === fieldIdA);
      const fieldInfoB = fieldsToShow.find((f) => f.field_id === fieldIdB);
      const isImportantA = fieldInfoA?.report_important === true;
      const isImportantB = fieldInfoB?.report_important === true;
      if (isImportantA && !isImportantB) return -1;
      if (!isImportantA && isImportantB) return 1;
      const valueA = calculationA.value || 0;
      const valueB = calculationB.value || 0;
      return valueB - valueA;
    });

    const formatValue = (value, calculation, operation = null) => {
      if (value === undefined || value === null) return "0";
      if (calculation.operator?.toLowerCase() === "percentage") return `${value.toFixed(2)}%`;
      const operator = calculation.operator || operation;
      if (operator?.toLowerCase() === "avg" || operator?.toLowerCase() === "average") return value.toFixed(2);
      if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2, useGrouping: false });
      return value.toString();
    };

    const getOperatorColor = (operator) => {
      const colors = { sum: "#1890ff", count: "#52c41a", average: "#fa8c16", percentage: "#eb2f96", max: "#722ed1", min: "#13c2c2" };
      return colors[operator?.toLowerCase()] || "#1890ff";
    };

    const formatFieldNameId = (fieldId) => fieldId.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

    const getActiveFilters = () => {
      const list = [];
      const divisionField = formFields.find((f) => f && (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग"))) || "division_name";
      let divisionValue = filters.division || filters.division_id;
      const isDivisionAdmin = !hasFullAccess() && (userRole === "DIVISION_ADMIN" || userRole === "division_admin" || userRole === "DIVISIONAL_OFFICER" || userRole === "divisional_officer" || userRole === "Divisionalcommissioner");
      if (!divisionValue && isDivisionAdmin && userDivisionId) {
        const divisionName = referenceCache.divisions[userDivisionId]?.name;
        if (divisionName) divisionValue = divisionName;
      }
      if (divisionValue) list.push({ type: "division", label: "Division", value: divisionValue, fieldId: divisionField, filterValue: divisionValue });
      const districtField = formFields.find((f) => f && (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा"))) || "district_name";
      const districtValue = filters.district || filters.district_id;
      if (districtValue) list.push({ type: "district", label: "District", value: districtValue, fieldId: districtField, filterValue: districtValue });
      const ulbField = formFields.find((f) => f && (f.toLowerCase().includes("ulb") || f.toLowerCase().includes("मनपा") || f.toLowerCase().includes("municipality"))) || "ulb_name";
      if (filters.ulb_name) list.push({ type: "ulb", label: "ULB", value: filters.ulb_name, fieldId: ulbField, filterValue: filters.ulb_name });
      const ulbTypeField = formFields.find((f) => f && (f.toLowerCase().includes("ulb_type") || f.toLowerCase().includes("type"))) || "ulb_type";
      if (filters.ulb_type) list.push({ type: "ulb_type", label: "ULB Type", value: filters.ulb_type, fieldId: ulbTypeField, filterValue: filters.ulb_type });
      const classField = formFields.find((f) => f && (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग"))) || "class";
      if (filters.ulb_class) list.push({ type: "ulb_class", label: "ULB Class", value: filters.ulb_class, fieldId: classField, filterValue: filters.ulb_class });
      return list;
    };

    const activeFilters = getActiveFilters();

    const getHierarchicalBreakdown = (fieldId, calculation, activeFilters) => {
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0 ? quickFilter.filteredData : formData;
      if (!dataToUse || dataToUse.length === 0) return [];
      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";
      const sampleRow = dataToUse[0];
      const possibleDivisionFields = ['division_name', 'division', 'Division', 'विभाग', 'division_name_formatted', 'division_label'];
      const possibleDistrictFields = ['district_name', 'district', 'District', 'जिल्हा', 'district_name_formatted', 'district_label'];
      const possibleUlbFields = ['ulb_name', 'ulb', 'ULB Name', 'मनपा/न.प. चे नाव', 'municipality', 'ulb_label', 'ULB'];
      let divisionField = null;
      for (const field of possibleDivisionFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") { divisionField = field; break; }
      }
      let districtField = null;
      for (const field of possibleDistrictFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") { districtField = field; break; }
      }
      let ulbField = null;
      for (const field of possibleUlbFields) {
        if (sampleRow && sampleRow[field] !== undefined && sampleRow[field] !== null && sampleRow[field] !== "") { ulbField = field; break; }
      }
      const divisionFilter = activeFilters.find((f) => f.type === "division");
      const districtFilter = activeFilters.find((f) => f.type === "district");
      const isDivisionAdmin = !hasFullAccess() && (userRole === "DIVISION_ADMIN" || userRole === "division_admin" || userRole === "DIVISIONAL_OFFICER" || userRole === "divisional_officer" || userRole === "Divisionalcommissioner");
      const isDistrictAdmin = !hasFullAccess() && (userRole === "DISTRICT_ADMIN" || userRole === "district_admin");
      let groupByField = null;
      let breakdownLevel = "";
      if (activeFilters.some(f => f.type === "ulb")) { groupByField = null; breakdownLevel = "record"; }
      else if (isDistrictAdmin && ulbField) { groupByField = ulbField; breakdownLevel = "ulb"; }
      else if (districtFilter && ulbField && !activeFilters.some(f => f.type === "ulb")) { groupByField = ulbField; breakdownLevel = "ulb"; }
      else if (isDivisionAdmin && districtField && !districtFilter) { groupByField = districtField; breakdownLevel = "district"; }
      else if (divisionFilter && districtField && !districtFilter && !activeFilters.some(f => f.type === "ulb")) { groupByField = districtField; breakdownLevel = "district"; }
      else if (divisionField) { groupByField = divisionField; breakdownLevel = "division"; }
      else if (districtField) { groupByField = districtField; breakdownLevel = "district"; }
      else if (ulbField) { groupByField = ulbField; breakdownLevel = "ulb"; }
      else { return []; }
      if (!groupByField || breakdownLevel === "record") {
        const results = [];
        dataToUse.forEach((row, idx) => {
          const val = row[actualColumnName];
          if (val !== undefined && val !== null && val !== "") {
            const numVal = parseFloat(String(val).replace(/,/g, ""));
            if (!isNaN(numVal) && isFinite(numVal)) {
              results.push({ name: `Record ${idx + 1}`, value: numVal, count: 1, avgValue: numVal, breakdownLevel: "record", division: row[divisionField] || row.division_name || row.division, district: row[districtField] || row.district_name || row.district, ulb: row[ulbField] || row.ulb_name || row.ulb });
            }
          }
        });
        return results;
      }
      const divisionFieldForFilter = divisionField || 'division_name';
      const districtFieldForFilter = districtField || 'district_name';
      const filterConditions = (row) => {
        let matches = true;
        if (divisionFilter) {
          const rowDivision = row[divisionFieldForFilter];
          if (rowDivision && String(rowDivision) !== String(divisionFilter.filterValue)) matches = false;
        }
        if (districtFilter && matches) {
          const rowDistrict = row[districtFieldForFilter];
          if (rowDistrict && String(rowDistrict) !== String(districtFilter.filterValue)) matches = false;
        }
        return matches;
      };
      const groups = {};
      dataToUse.forEach((row) => {
        if (!filterConditions(row)) return;
        let groupValue = row[groupByField];
        if (!groupValue || groupValue === null || groupValue === undefined || groupValue === "") groupValue = `Unknown ${breakdownLevel}`;
        const val = row[actualColumnName];
        if (val !== undefined && val !== null && val !== "") {
          const numVal = parseFloat(String(val).replace(/,/g, ""));
          if (!isNaN(numVal) && isFinite(numVal)) {
            if (!groups[groupValue]) groups[groupValue] = { name: groupValue, values: [], division: row[divisionFieldForFilter] || row.division_name || row.division, district: row[districtFieldForFilter] || row.district_name || row.district, ulb: breakdownLevel === "ulb" ? row[groupByField] : row[ulbField] || row.ulb_name || row.ulb };
            groups[groupValue].values.push(numVal);
          }
        }
      });
      const results = [];
      Object.keys(groups).forEach((groupName) => {
        const group = groups[groupName];
        let result = 0;
        if (group.values.length > 0) {
          switch (operator.toLowerCase()) {
            case "sum": result = group.values.reduce((a, b) => a + b, 0); break;
            case "average": case "avg": result = group.values.reduce((a, b) => a + b, 0) / group.values.length; break;
            case "min": result = Math.min(...group.values); break;
            case "max": result = Math.max(...group.values); break;
            case "count": result = group.values.length; break;
            default: result = group.values.reduce((a, b) => a + b, 0);
          }
        }
        results.push({ name: groupName, value: result, count: group.values.length, avgValue: group.values.length > 0 ? result / group.values.length : 0, breakdownLevel: breakdownLevel, division: group.division, district: group.district, ulb: group.ulb });
      });
      return results.sort((a, b) => a.name.localeCompare(b.name));
    };

    const getFilterFieldForType = (filterType) => {
      switch (filterType) {
        case "division": return formFields.find(f => f && (f.toLowerCase().includes("division") || f.toLowerCase().includes("विभाग"))) || "division_name";
        case "district": return formFields.find(f => f && (f.toLowerCase().includes("district") || f.toLowerCase().includes("जिल्हा"))) || "district_name";
        case "ulb": return formFields.find(f => f && (f.toLowerCase().includes("ulb") || f.toLowerCase().includes("मनपा") || f.toLowerCase().includes("municipality"))) || "ulb_name";
        case "ulb_type": return formFields.find(f => f && (f.toLowerCase().includes("ulb_type") || f.toLowerCase().includes("type"))) || "ulb_type";
        case "ulb_class": return formFields.find(f => f && (f.toLowerCase().includes("class") || f.toLowerCase().includes("वर्ग"))) || "class";
        default: return null;
      }
    };

    const getFilterSpecificValue = (fieldId, calculation, currentFilterType, currentFilterValue, allActiveFilters) => {
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0 ? quickFilter.filteredData : (originalFormData && originalFormData.length > 0 ? originalFormData : formData);
      if (!dataToUse || dataToUse.length === 0 || !currentFilterValue) return null;
      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";
      let filteredData = [...dataToUse];
      const filterOrder = { ulb_type: 0, ulb_class: 1, division: 2, district: 3, ulb: 4 };
      const currentIndex = filterOrder[currentFilterType] || 0;
      for (const filter of allActiveFilters) {
        if (filter.type === currentFilterType) continue;
        const filterIndex = filterOrder[filter.type] || 0;
        if (filterIndex < currentIndex) {
          const filterField = getFilterFieldForType(filter.type);
          if (filterField) filteredData = filteredData.filter((row) => { const rowValue = row[filterField]; return rowValue && String(rowValue) === String(filter.filterValue); });
        }
      }
      const currentFilterField = getFilterFieldForType(currentFilterType);
      if (!currentFilterField) return null;
      filteredData = filteredData.filter((row) => { const rowValue = row[currentFilterField]; return rowValue && String(rowValue) === String(currentFilterValue); });
      if (filteredData.length === 0) return null;
      const values = filteredData.map((row) => {
        const val = row[actualColumnName];
        if (val === undefined || val === null || val === "") return null;
        const numVal = parseFloat(String(val).replace(/,/g, ""));
        return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
      }).filter((v) => v !== null);
      if (values.length === 0) return null;
      switch (operator.toLowerCase()) {
        case "sum": return values.reduce((a, b) => a + b, 0);
        case "avg": case "average": return values.reduce((a, b) => a + b, 0) / values.length;
        case "min": return Math.min(...values);
        case "max": return Math.max(...values);
        case "count": return values.length;
        default: return values.reduce((a, b) => a + b, 0);
      }
    };

    const handleApplyFilter = (filterType, filterValue, value, fieldInfo, calculation) => {
      message.success(`Applied ${filterType} filter: ${filterValue}`);
      if (filterType === "division") handleDivisionChange(filterValue);
      else if (filterType === "district") handleDistrictChange(filterValue);
      else if (filterType === "ulb") handleUlbChange(filterValue);
      if (form) setTimeout(() => loadFormData(form.id, { ...filters, [filterType]: filterValue }), 100);
    };

    const handleClearFilters = () => { message.info("Cleared all filters"); resetFilters(); };

    const calculateStateTotal = (fieldId, calculation) => {
      const dataToUse = quickFilter.isActive && quickFilter.filteredData.length > 0 ? quickFilter.filteredData : (originalFormData.length > 0 ? originalFormData : formData);
      const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
      const actualColumnName = fieldInfo?.column_name || fieldId;
      const operator = calculation.operator || "sum";
      const allValues = dataToUse.map((row) => {
        const val = row[actualColumnName];
        if (val === undefined || val === null || val === "") return null;
        const numVal = parseFloat(String(val).replace(/,/g, ""));
        return !isNaN(numVal) && isFinite(numVal) ? numVal : null;
      }).filter((v) => v !== null);
      let originalTotal = 0;
      if (allValues.length > 0) {
        switch (operator.toLowerCase()) {
          case "sum": originalTotal = allValues.reduce((a, b) => a + b, 0); break;
          case "avg": case "average": originalTotal = allValues.reduce((a, b) => a + b, 0) / allValues.length; break;
          case "min": originalTotal = Math.min(...allValues); break;
          case "max": originalTotal = Math.max(...allValues); break;
          case "count": originalTotal = allValues.length; break;
          default: originalTotal = allValues.reduce((a, b) => a + b, 0);
        }
      }
      return formatValue(originalTotal, calculation);
    };

    return (
      <div style={{ marginBottom: 22 }}>
        <Card title={null} style={{ width: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: "12px" }} bodyStyle={{ padding: "0", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ background: "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)", padding: "9px 11px", borderRadius: "12px 12px 0 0", flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
              <span style={{ fontWeight: 600, fontSize: "13px", color: "#ffffff" }}>{quickFilter.isActive ? "FILTERED DATA POINT VIEW" : "DATA POINT VIEW"}</span>
              {activeFilters.length > 0 && <Button size="small" onClick={handleClearFilters} style={{ fontSize: "11px", height: "28px" }}><ReloadOutlined /> Clear Filters ({activeFilters.length})</Button>}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
            <div style={{ display: viewMode === "double" ? "grid" : "flex", gridTemplateColumns: viewMode === "double" ? "repeat(2, 1fr)" : "none", flexDirection: viewMode === "double" ? "row" : "column", gap: "16px", maxHeight: "calc(100vh - 300px)", overflowY: "auto", padding: "4px" }}>
              {sortedAllFields.map(([fieldId, calculation]) => {
                const fieldInfo = fieldsToShow.find((f) => f.field_id === fieldId);
                const displayName = fieldInfo?.display_name || calculation.display_name || formatFieldNameId(fieldId);
                const operator = calculation.operator || "sum";
                const columnName = fieldInfo?.column_name || fieldId;
                const hierarchicalBreakdown = getHierarchicalBreakdown(fieldId, calculation, activeFilters);
                const breakdownLevel = hierarchicalBreakdown.length > 0 ? hierarchicalBreakdown[0].breakdownLevel : "division";
                const getBreakdownLabel = (level) => { const labels = { division: "Divisions", district: "Districts", ulb: "ULBs" }; return labels[level] || "Breakdown"; };
                const breakdownLabel = getBreakdownLabel(breakdownLevel);
                const graphKey = fieldId;
                const graphData = fieldGraphsData[graphKey];
                const isLoadingGraph = loadingGraphs[graphKey];
                if (!graphData && !isLoadingGraph) setTimeout(() => generateColumnGraph(columnName, displayName, fieldId, "pie"), 100);
                return (
                  <div key={fieldId} style={{ padding: "20px", border: "1px solid #e9ecef", borderRadius: "12px", background: "#ffffff", transition: "all 0.2s ease", position: "relative", height: "fit-content" }} onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = getOperatorColor(operator); }} onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.03)"; e.currentTarget.style.borderColor = "#e9ecef"; }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: getOperatorColor(operator), borderRadius: "3px 3px 0 0" }} />
                    {viewMode === "single" ? (
                      <div style={{ display: "flex", minHeight: "280px" }}>
                        <div style={{ flex: 1, paddingRight: "8px", display: "flex", flexDirection: "column", overflow: "auto" }}>
                          <div style={{ fontWeight: 600, fontSize: "16px", color: "#1a1a1a", marginBottom: "12px" }}>{displayName}</div>
                          <div style={{ textAlign: "start", padding: "8px 12px", background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5", borderRadius: "8px", display: "inline-block", width: "fit-content", marginBottom: "16px", borderLeft: quickFilter.isActive ? "3px solid #1890ff" : "none" }}>
                            <div style={{ fontSize: "12px", color: quickFilter.isActive ? "#1890ff" : "black" }}>{quickFilter.isActive ? `Filtered Total (${quickFilter.fieldDisplayName} = ${quickFilter.value})` : "State Total"}</div>
                            <div style={{ fontSize: "18px", fontWeight: 700, color: "#1890ff", lineHeight: 1.2 }}>{calculateStateTotal(fieldId, calculation)}</div>
                            {quickFilter.isActive && <div style={{ fontSize: "10px", color: "#8c8c8c", marginTop: "4px" }}>Based on {quickFilter.filteredData.length} matching records</div>}
                          </div>
                          {activeFilters.length > 0 && (
                            <div style={{ marginBottom: "16px", paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>
                              <div style={{ fontSize: "10px", fontWeight: 600, color: "#595959", marginBottom: "8px", textTransform: "uppercase" }}>Active Filters</div>
                              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                {activeFilters.map((filter, idx) => {
                                  const filterValue = getFilterSpecificValue(fieldId, calculation, filter.type, filter.filterValue, activeFilters);
                                  if (filterValue === null) return null;
                                  return (
                                    <div key={`${filter.type}-${idx}`} style={{ flex: "1", minWidth: "140px", textAlign: "start", padding: "8px 12px", background: "#f5f5f5", borderRadius: "8px", display: "inline-block", width: "fit-content" }}>
                                      <div style={{ fontSize: "11px", color: "black", marginBottom: "2px" }}>{filter.label}</div>
                                      <div style={{ fontSize: "15px", fontWeight: 700, color: "#fa8c16", lineHeight: 1.2, wordBreak: "break-word" }}>{filter.filterValue}</div>
                                      <div style={{ fontSize: "11px", color: "black", marginTop: "4px" }}>{formatValue(filterValue, calculation)}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {hierarchicalBreakdown.length > 0 && (
                            <div style={{ marginBottom: "16px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: breakdownLevel === "ulb" ? "#fa8c16" : (breakdownLevel === "district" ? "#52c41a" : "#1890ff"), textTransform: "uppercase" }}>{breakdownLabel} ({hierarchicalBreakdown.length})</div>
                                <div style={{ flex: 1, height: "1px", background: "#e9ecef" }} />
                              </div>
                              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                                {hierarchicalBreakdown.slice(0, 20).map((item, idx) => {
                                  const colorFamilies = [{ start: "#096dd9", end: "#69c0ff" }, { start: "#389e0d", end: "#95de64" }, { start: "#d46b08", end: "#ffc069" }, { start: "#cf1322", end: "#ff7875" }, { start: "#531dab", end: "#b37feb" }, { start: "#08979c", end: "#5cdbd3" }, { start: "#c41d7f", end: "#ff85c0" }, { start: "#d48806", end: "#ffd666" }, { start: "#7cb305", end: "#bae637" }, { start: "#1d39c4", end: "#597ef7" }, { start: "#ee5a5a", end: "#ff9c9c" }, { start: "#3dbdb5", end: "#87e8de" }, { start: "#35a7c1", end: "#8fd4e8" }, { start: "#86bea4", end: "#cce8d4" }, { start: "#eeda97", end: "#fff5cc" }];
                                  const colors = colorFamilies[idx % colorFamilies.length];
                                  let clickMessage = "";
                                  if (breakdownLevel === "division") clickMessage = "Click to view districts";
                                  else if (breakdownLevel === "district") clickMessage = "Click to view ULBs";
                                  else if (breakdownLevel === "ulb") clickMessage = "Click to filter ULB data";
                                  return (
                                    <div key={idx} style={{ background: `linear-gradient(135deg, ${colors.start} 0%, ${colors.end} 100%)`, borderRadius: "10px", padding: "12px 8px", border: "1px solid rgba(255,255,255,0.3)", textAlign: "center", cursor: "pointer", transition: "all 0.3s ease", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
                                      onClick={() => { if (breakdownLevel === "division") handleApplyFilter("division", item.name, item.value, fieldInfo, calculation); else if (breakdownLevel === "district") handleApplyFilter("district", item.name, item.value, fieldInfo, calculation); else if (breakdownLevel === "ulb") handleApplyFilter("ulb", item.name, item.value, fieldInfo, calculation); }}
                                      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px) scale(1.02)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.3)"; e.currentTarget.style.border = "1px solid rgba(255,255,255,0.5)"; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0) scale(1)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; e.currentTarget.style.border = "1px solid rgba(255,255,255,0.3)"; }}
                                    >
                                      <div style={{ fontSize: "11px", fontWeight: 600, color: "#ffffff", marginBottom: "6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{item.name}</div>
                                      <div style={{ fontSize: "15px", fontWeight: 800, color: "#ffffff", textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>{formatValue(item.value, calculation)}</div>
                                      {clickMessage && <div style={{ fontSize: "8px", color: "rgba(255,255,255,0.7)", marginTop: "4px" }}>{clickMessage}</div>}
                                    </div>
                                  );
                                })}
                              </div>
                              {hierarchicalBreakdown.length > 20 && <div style={{ textAlign: "center", marginTop: "8px", fontSize: "10px", color: "#8c8c8c" }}>+{hierarchicalBreakdown.length - 20} more {breakdownLabel.toLowerCase()}</div>}
                            </div>
                          )}
                          {hierarchicalBreakdown.length === 0 && <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>No {breakdownLabel.toLowerCase()} available</div>}
                        </div>
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                          {isLoadingGraph ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "300px", flexDirection: "column", gap: "12px" }}>
                              <Spin size="large" />
                              <div style={{ fontSize: "11px", color: "#8c8c8c" }}>Loading graph data...</div>
                            </div>
                          ) : graphData && graphData.isGraphReady && graphData.data && graphData.data.length > 0 ? (
                            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px", gap: "4px" }}>
                                <Tooltip title="Bar Chart"><Button size="small" type={graphData.chartType !== "pie" ? "primary" : "default"} icon={<BarChartOutlined />} onClick={(e) => { e.stopPropagation(); toggleChartType(graphKey); }} style={{ padding: "0 6px", height: "24px", fontSize: "11px" }} /></Tooltip>
                                <Tooltip title="Pie Chart"><Button size="small" type={graphData.chartType === "pie" ? "primary" : "default"} icon={<PieChartOutlined />} onClick={(e) => { e.stopPropagation(); toggleChartType(graphKey); }} style={{ padding: "0 6px", height: "24px", fontSize: "11px" }} /></Tooltip>
                                <Tooltip title="Full Screen"><Button size="small" icon={<FullscreenOutlined />} onClick={async (e) => { e.stopPropagation(); setGraphModalData(graphData); setGraphModalVisible(true); }} style={{ padding: "0 6px", height: "24px", fontSize: "11px" }} /></Tooltip>
                              </div>
                              <div style={{ flex: 1, minHeight: 0, transition: "transform 0.2s ease", background: "#FFFFFF", borderRadius: "8px", padding: "8px" }}>
                                {graphData.chartType === "pie" ? (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                      <Pie data={graphData.data.slice(0, 12).map((item) => ({ name: item.name, shortName: item.name.length > 15 ? item.name.substring(0, 12) + ".." : item.name, fullName: item.name, value: item.value, originalName: item.originalName || item.name }))} cx="50%" cy="50%" innerRadius={35} outerRadius={75} paddingAngle={2} dataKey="value" label={({ shortName, name, percent }) => { const percentage = (percent * 100).toFixed(1); const displayName = shortName || name; return `${displayName}: ${percentage}%`; }} labelLine={true} isAnimationActive={false} stroke="none">
                                        {graphData.data.slice(0, 12).map((entry, idx) => <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} stroke="#fff" strokeWidth={2} style={{ cursor: "pointer" }} />)}
                                      </Pie>
                                      <RechartsTooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                          const data = payload[0].payload;
                                          const totalValue = graphData.data.slice(0, 12).reduce((sum, item) => sum + item.value, 0);
                                          const percentage = ((data.value / totalValue) * 100).toFixed(1);
                                          const currentLevel = graphData.aggregationLevel || breakdownLevel;
                                          let drillHint = "";
                                          if (currentLevel === "division") drillHint = "Click to view districts";
                                          else if (currentLevel === "district") drillHint = "Click to view ULBs";
                                          else if (currentLevel === "ulb") drillHint = "Click to filter ULB data";
                                          return (
                                            <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", minWidth: "200px" }}>
                                              <div><strong>{data.fullName || data.name}</strong></div>
                                              <div>Value: {data.value.toLocaleString()}</div>
                                              <div>Percentage: {percentage}%</div>
                                              {data.count && <div>Records: {data.count}</div>}
                                              {drillHint && <div style={{ marginTop: "6px", fontSize: "10px", color: "#1890ff", borderTop: "1px solid #f0f0f0", paddingTop: "4px" }}>💡 {drillHint}</div>}
                                            </div>
                                          );
                                        }
                                        return null;
                                      }} />
                                    </PieChart>
                                  </ResponsiveContainer>
                                ) : (
                                  <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={graphData.data} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} interval={0} tick={{ fontSize: 9 }} />
                                      <YAxis tick={{ fontSize: 9 }} tickFormatter={(value) => { if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`; if (value >= 1000) return `${(value / 1000).toFixed(0)}K`; return value.toLocaleString(); }} />
                                      <RechartsTooltip content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                          const data = payload[0].payload;
                                          const currentLevel = graphData.aggregationLevel || breakdownLevel;
                                          let drillHint = "";
                                          if (currentLevel === "division") drillHint = "Click to view districts";
                                          else if (currentLevel === "district") drillHint = "Click to view ULBs";
                                          else if (currentLevel === "ulb") drillHint = "Click to filter ULB data";
                                          return (
                                            <div style={{ background: "#fff", padding: "8px 12px", borderRadius: "8px", fontSize: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                                              <div><strong>{data.originalName || data.name}</strong></div>
                                              <div>Value: {data.value.toLocaleString()}</div>
                                              {data.count && <div>Records: {data.count}</div>}
                                              {drillHint && <div style={{ marginTop: "6px", fontSize: "10px", color: "#1890ff", borderTop: "1px solid #f0f0f0", paddingTop: "4px" }}>💡 {drillHint}</div>}
                                            </div>
                                          );
                                        }
                                        return null;
                                      }} />
                                      <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} cursor="pointer" />
                                    </BarChart>
                                  </ResponsiveContainer>
                                )}
                              </div>
                              {graphData.data.length > 0 && (
                                <div style={{ marginTop: "8px", fontSize: "10px", color: "#8c8c8c", textAlign: "center", borderTop: "1px solid #f0f0f0", paddingTop: "6px" }}>
                                  Total: {graphData.total?.toLocaleString() || graphData.data.reduce((sum, item) => sum + item.value, 0).toLocaleString()} • {graphData.data.length} {graphData.groupLabel || "items"} • {graphData.aggregationLevel === "district" ? "📊 District Level" : graphData.aggregationLevel === "division" ? "🏢 Division Level" : graphData.aggregationLevel === "ulb" ? "🏛️ ULB Level" : "Record Level"}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "300px", background: "#F5F5F5", borderRadius: "8px", flexDirection: "column", gap: "12px" }}>
                              <Spin size="large" />
                              <div style={{ fontSize: "11px", color: "#8c8c8c" }}>Loading graph data...</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>{displayName}</div>
                            <div style={{ textAlign: "start", padding: "4px 10px", background: quickFilter.isActive ? "#e6f7ff" : "#f5f5f5", borderRadius: "6px", display: "inline-block", marginTop: "6px", borderLeft: quickFilter.isActive ? "2px solid #1890ff" : "none" }}>
                              <div style={{ fontSize: "12px", color: quickFilter.isActive ? "#1890ff" : "black" }}>{quickFilter.isActive ? "Filtered Total" : "State Total"}</div>
                              <div style={{ fontSize: "16px", fontWeight: 700, color: "#1890ff", lineHeight: 1.2 }}>{calculateStateTotal(fieldId, calculation)}</div>
                            </div>
                          </div>
                          <Button type="primary" icon={<BarChartOutlined />} onClick={async (e) => { e.stopPropagation(); if (graphData && graphData.isGraphReady) { setGraphModalData(graphData); setGraphModalVisible(true); } else { setGraphModalLoading(true); setGraphModalVisible(true); const generatedData = await generateColumnGraph(columnName, displayName, fieldId, "pie"); if (generatedData) setGraphModalData(generatedData); setGraphModalLoading(false); } }} loading={isLoadingGraph} size="small" style={{ borderRadius: "8px", fontWeight: 500, fontSize: "12px" }}>Graphical View</Button>
                        </div>
                        {activeFilters.length > 0 && (
                          <div style={{ marginBottom: "16px", paddingTop: "8px", borderTop: "1px solid #f0f0f0" }}>
                            <div style={{ fontSize: "10px", fontWeight: 600, color: "#595959", marginBottom: "8px", textTransform: "uppercase" }}>Active Filters</div>
                            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                              {activeFilters.map((filter, idx) => {
                                const filterValue = getFilterSpecificValue(fieldId, calculation, filter.type, filter.filterValue, activeFilters);
                                if (filterValue === null) return null;
                                return (
                                  <div key={`${filter.type}-${idx}`} style={{ flex: "1", minWidth: "140px", textAlign: "start", padding: "8px 12px", background: "#f5f5f5", borderRadius: "8px", display: "inline-block", width: "fit-content" }}>
                                    <div style={{ fontSize: "11px", color: "#8c8c8c", marginBottom: "2px" }}>{filter.label}</div>
                                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#fa8c16", lineHeight: 1.2, wordBreak: "break-word" }}>{filter.filterValue}</div>
                                    <div style={{ fontSize: "11px", color: "#8c8c8c", marginTop: "4px" }}>{formatValue(filterValue, calculation)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", marginTop: "8px" }}>
                          {hierarchicalBreakdown.slice(0, 15).map((item, idx) => (
                            <div key={idx} style={{ background: "#fafafa", borderRadius: "6px", padding: "8px 6px", border: "1px solid #f0f0f0", textAlign: "center", cursor: "pointer", transition: "all 0.2s ease" }}
                              onClick={() => { if (breakdownLevel === "division") handleApplyFilter("division", item.name, item.value, fieldInfo, calculation); else if (breakdownLevel === "district") handleApplyFilter("district", item.name, item.value, fieldInfo, calculation); else if (breakdownLevel === "ulb") handleApplyFilter("ulb", item.name, item.value, fieldInfo, calculation); }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f0f0"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "#fafafa"; e.currentTarget.style.transform = "translateY(0)"; }}
                            >
                              <div style={{ fontSize: "12px", fontWeight: 500, color: "#333", marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.name}>{item.name}</div>
                              <div style={{ fontSize: "14px", fontWeight: 700, color: getOperatorColor(operator) }}>{formatValue(item.value, calculation)}</div>
                              {item.count && <div style={{ fontSize: "9px", color: "#bfbfbf", marginTop: "2px" }}>{item.count} records</div>}
                            </div>
                          ))}
                        </div>
                        {hierarchicalBreakdown.length === 0 && <div style={{ textAlign: "center", padding: "20px", color: "#8c8c8c" }}>No metrics available</div>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div style={{ padding: "16px", height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Form Field Filter Component - This is the key addition! */}
      <FormFieldFilter
        selectedForm={form}
        formData={formData}
        formFields={formFields}
        fieldLabels={fieldLabels}
        referenceCache={referenceCache}
        onFilterChange={(field, value, filteredData, fieldDisplayName) => {
          console.log("📢 Received filter change:", { field, value, filteredDataLength: filteredData?.length, fieldDisplayName });
          setQuickFilter({
            field: field,
            value: value,
            filteredData: filteredData || [],
            isActive: field && value,
            fieldDisplayName: fieldDisplayName || field
          });
          if (field && value && filteredData && filteredData.length > 0) {
            const calculations = processReportCalculations(filteredData, reportEnabledFields);
            setReportCalculations(calculations);
          } else if (originalFormData.length > 0) {
            const calculations = processReportCalculations(originalFormData, reportEnabledFields);
            setReportCalculations(calculations);
          }
        }}
      />

      <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
  <Title level={3} style={{ margin: 0 }}>{form.displayName || form.name || `Form ${form.id}`}</Title>
  <div style={{ display: "flex", gap: "10px" }}>
    {/* View All Data Button */}
    <Button type="primary" icon={<EyeOutlined />} onClick={() => setDataModalVisible(true)}>
      View All Data
    </Button>
    {/* Back to Forms Button */}
    <Button icon={<LeftOutlined />} onClick={onBack}>
      Back to Forms
    </Button>
  </div>
</div>

      {renderFilterSection()}

      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: "16px" }}>
        <div style={{ width: "320px", flexShrink: 0, borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", height: containerHeight, display: "flex", flexDirection: "column", border: "1px solid #f0f0f0", background: "rgba(255, 255, 255, 0.95)", overflow: "hidden" }}>
          <div style={{ background: "linear-gradient(90deg, #020617 0%, #0F172A 40%, #0F766E 100%)", padding: "9px 11px", color: "#ffffff", fontWeight: 600, fontSize: "14px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Space><span>Data Points</span></Space>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "8px" }}>
            {reportEnabledFields.length === 0 ? (
              <Empty description="No data points available" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "40px 0" }} />
            ) : (
              reportEnabledFields.map((record, idx) => {
                const isSelected = selectedDataPointId === record.field_id;
                const gradientData = DATAPOINT_GRADIENT_SHADES[idx % DATAPOINT_GRADIENT_SHADES.length];
                return (
                  <div key={record.field_id} onClick={() => { if (selectedDataPointId === record.field_id) setSelectedDataPointId(null); else setSelectedDataPointId(record.field_id); }} style={{
                    cursor: "pointer", padding: "12px 16px", marginBottom: "8px", background: `linear-gradient(135deg, ${gradientData.start}, ${gradientData.end})`, borderRadius: "8px",
                    borderLeft: isSelected ? `4px solid #ffd700` : "3px solid transparent", borderRight: isSelected ? "1px solid rgba(255,215,0,0.3)" : "none",
                    transition: "all 0.2s ease", boxShadow: isSelected ? "0 4px 12px rgba(0,0,0,0.25)" : "0 1px 3px rgba(0,0,0,0.1)", opacity: 1
                  }}
                  onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.transform = "translateX(4px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)"; e.currentTarget.style.borderLeft = "3px solid rgba(255,255,255,0.5)"; } }}
                  onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.transform = "translateX(0)"; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)"; e.currentTarget.style.borderLeft = "3px solid transparent"; } }}
                  >
                    <div style={{ fontWeight: isSelected ? 700 : 600, fontSize: "13px", color: "#ffffff", wordBreak: "break-word", textShadow: "0 1px 2px rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>{record.display_name || record.field_id}</span>
                      <span>{record.report_important && <StarOutlined style={{ color: "#ffd700", marginLeft: "8px", fontSize: "11px" }} />}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0, height: containerHeight, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {selectedDataPointId && (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#e6f7ff", borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span><EyeOutlined style={{ color: "#1890ff", marginRight: 8 }} /> Showing only: <strong>{reportEnabledFields.find(f => f.field_id === selectedDataPointId)?.display_name || selectedDataPointId}</strong></span>
              <Button size="small" type="link" onClick={() => setSelectedDataPointId(null)}>Show All</Button>
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {(() => {
              const fieldsToShow = selectedDataPointId ? reportEnabledFields.filter(field => field.field_id === selectedDataPointId) : reportEnabledFields;
              return fieldsToShow.length === 0 ? <Empty description="No data points available" image={Empty.PRESENTED_IMAGE_SIMPLE} /> : renderReportEnabledCardsWithFilter(fieldsToShow);
            })()}
          </div>
        </div>
      </div>

      {renderDataModal()}
      {renderGraphModal()}
      {error && <Alert message="Error" description={error} type="error" showIcon style={{ marginTop: "16px" }} />}
    </div>
  );
};

export default FormSelectionView;